<template>
  <div>
    <validation-observer ref="observer">
      <labeled-wrapper :label="$t('financial_actions') | capitalize"
        v-if="distribution.distributionMethodology !== distributionMethodologies.STANDARD"
        style="margin-bottom: 25px">
        <b-row
          v-if="distribution.distributionMethodology !== distributionMethodologies.INTERNATIONAL &&
          distribution.distributionMethodology !== distributionMethodologies.CONNECT &&
          !connectTariffPeriod">
          <b-col cols="6">
            <distribution-detail-field
                :label="$t('hold') | capitalize"
                vid="hold"
                ref="hold"
                :edit-mode="editMode"
                :value="holdDisplay(distribution.hold)"
            >
              <template v-slot:input="{ state }">
                <b-form-select v-model="distribution.hold" :options="holdOptions" :state="state">
                  <template v-slot:first>
                    <b-form-select-option :value="null">
                      {{ $t("none") | capitalize }}
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </template>
            </distribution-detail-field>
          </b-col>

        </b-row>
        <b-row>
          <b-col cols="6">
            <distribution-detail-field
                :label="$t('calculate_withholding') | capitalize"
                vid="calculateWithholding"
                ref="calculateWithholding"
                :edit-mode="editMode"
                :value="distribution.calculateWithholding ? $t('yes') : $t('no') | capitalize"
            >
              <template v-slot:input="{ state }">
                <b-form-select v-model="distribution.calculateWithholding" :state="state">
                  <b-form-select-option :value="true">{{ $t("yes") | capitalize }}</b-form-select-option>
                  <b-form-select-option :value="false">{{ $t("no") | capitalize }}</b-form-select-option>
                </b-form-select>
              </template>
            </distribution-detail-field>
          </b-col>
        </b-row>
      </labeled-wrapper>
      <labeled-wrapper
          :label="$t('reference_information') | capitalize">
        <b-row>
          <b-col cols="6">
            <distribution-detail-field
                :label="$t('tariff_period_id')"
                :value="distribution.id"
                ref="id"
                vid="id"
            />
          </b-col>
        </b-row>
      </labeled-wrapper>
      <labeled-wrapper
          :label="$t('additional_details') | capitalize"
          style="margin-top: 25px">
        <b-row>
          <b-col cols="12">
            <distribution-detail-field
                :label="$t('notes') | capitalize"
                :value="distribution.notes"
                :edit-mode="editMode"
                ref="notes"
                vid="notes"
                class="notes"
                :labelCols="3"
                :valueCols="9"
            >
              <template v-slot:input="{ state }">
                <b-form-textarea v-model="distribution.notes" :state="state"/>
              </template>
            </distribution-detail-field>
          </b-col>
        </b-row>
      </labeled-wrapper>
      <labeled-wrapper :label="$t('moratorium') | capitalize">
        <b-row>
          <b-col cols="6" v-if="distribution.college === distributionCollegeTypes.MAKER || distribution.college === distributionCollegeTypes.MAKER_AND_PERFORMER">
            <distribution-detail-field
                :label="`${$t('moratorium_minimum')} (${$t('maker')})` | capitalize"
                vid="moratoriumMakerMinimum"
                ref="moratoriumMakerMinimum"
                :edit-mode="editMode"
                :detect-input="false"
                :rules="'double|positive' + (distribution.moratoriumMakerMinimumType === distributionReserveValueTypes.PERCENT ? '|max_value:100' : '')"
            >
              <template v-slot:value>
                <span v-if="distribution.moratoriumMakerMinimumType === distributionReserveValueTypes.PERCENT">
                  {{ distribution.moratoriumMakerMinimum }}%
                </span>
                  <span v-else>
                  {{ distribution.revenueCurrency }} {{ distribution.moratoriumMakerMinimum }}
                </span>
              </template>
              <template v-slot:input="{ state, validate }">
                <b-input-group>
                  <template v-slot:append>
                    <b-form-select
                      :options="valueTypeOptions" v-model="distribution.moratoriumMakerMinimumType"
                      @change="validate(distribution.moratoriumMakerMinimum)"
                    />
                  </template>
                  <b-form-input
                      @hook:mounted="validate(distribution.moratoriumMakerMinimum)"
                      number
                      v-model="distribution.moratoriumMakerMinimum"
                      :state="state"
                      @input="validate"
                  />
                </b-input-group>
              </template>
            </distribution-detail-field>
          </b-col>
          <b-col cols="6" v-if="distribution.college === distributionCollegeTypes.PERFORMER || distribution.college === distributionCollegeTypes.MAKER_AND_PERFORMER">
            <distribution-detail-field
                :label="`${$t('moratorium_minimum')} (${$t('performer')})` | capitalize"
                vid="moratoriumPerformerMinimum"
                ref="moratoriumPerformerMinimum"
                :edit-mode="editMode"
                :detect-input="false"
                :rules="'double|positive' + (distribution.moratoriumPerformerMinimumType === distributionReserveValueTypes.PERCENT ? '|max_value:100' : '')"
            >
              <template v-slot:value>
                <span v-if="distribution.moratoriumPerformerMinimumType === distributionReserveValueTypes.PERCENT">
                  {{ distribution.moratoriumPerformerMinimum }}%
                </span>
                <span v-else>
                  {{ distribution.revenueCurrency }} {{ distribution.moratoriumPerformerMinimum }}
                </span>
              </template>
              <template v-slot:input="{ state, validate }">
                <b-input-group>
                  <template v-slot:append>
                    <b-form-select 
                      :options="valueTypeOptions"
                      v-model="distribution.moratoriumPerformerMinimumType"
                      @change="validate(distribution.moratoriumPerformerMinimum)"
                    />
                  </template>
                  <b-form-input
                      @hook:mounted="validate(distribution.moratoriumPerformerMinimum)"
                      number
                      v-model="distribution.moratoriumPerformerMinimum"
                      :state="state"
                      @input="validate"
                  />
                </b-input-group>
              </template>
            </distribution-detail-field>
          </b-col>
        </b-row>
      </labeled-wrapper>
    </validation-observer>
  </div>
</template>

<script>
import {
  canadaCurrencyCode,
  distributionCollegeTypes,
  distributionMethodologies,
  distributionReserveValueTypes
} from "@/constants"
import DistributionDetailField from "@/components/DistributionDetailField"
import LabeledWrapper from "../../../components/LabeledWrapper.vue"
import { ValidationObserver } from "vee-validate"
import { capitalize } from "lodash"
import { distributionSectionMixin } from "@/utils/mixins"

export default {
  name: "OtherSection",
  components: { LabeledWrapper, DistributionDetailField, ValidationObserver },
  mixins: [distributionSectionMixin],
  props: {
    distribution: Object,
    editMode: Boolean,
    connectTariffPeriod: Boolean,
  },
  data () {
    return {
      canadaCurrencyCode,
      distributionMethodologies,
      distributionReserveValueTypes,
      distributionCollegeTypes,
      holdOptions: Object.values(distributionCollegeTypes).map(v => ({ value: v, text: capitalize(this.$t(v)) })),
      valueTypeOptions: [
        {
          text: "%",
          value: distributionReserveValueTypes.PERCENT,
        },
        {
          text: "CAD",
          value: distributionReserveValueTypes.MONEY,
        }
      ]
    }
  },
  methods: {
    holdDisplay (value) {
      return this.holdOptions.find(o => o.value === value)?.text
    }
  }
}
</script>
<style lang="scss" scoped>
.notes {
  ::v-deep .label {
    align-content: flex-start !important;
  }
  ::v-deep .value {
    padding-left: 0;
    span {
      display: flex;
      flex-direction: row;
    }
    @media screen and (min-width: 1920px) {
      span {
        display: flex;
        align-items: flex-start;
      }
    }
  }
}
</style>
